import React, { useState, useEffect } from 'react';
import { Card, Typography, Radio, Skeleton } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { LocationExpense } from '../../../services/ExpenseService';
import { groupItemsByCriticalItem, CRITICAL_ITEMS_MAPPING } from '../utils';
import { formatCurrency } from '../../../utils/formatters';

const { Text } = Typography;

interface ExpenseMobileCardProps {
  locationExpense: LocationExpense;
  itemViewMode: Record<string, 'all' | 'critical'>;
  setItemViewMode: (mode: Record<string, 'all' | 'critical'>) => void;
  viewMode: 'weekly' | 'quarterly';
  getSalesForLocation: (locationName: string) => number;
  item8WeekAverages?: Record<string, { averageQuantity: number; averageExpense: number }>;
  item4WeekAverages?: Record<string, { averageQuantity: number; averageExpense: number }>;
  loadingAverages?: boolean;
  onExpand?: (expanded: boolean) => void;
}

export const ExpenseMobileCard: React.FC<ExpenseMobileCardProps> = ({
  locationExpense,
  itemViewMode,
  setItemViewMode,
  viewMode,
  getSalesForLocation,
  item8WeekAverages,
  item4WeekAverages,
  loadingAverages,
  onExpand,
}) => {
  const [showItems, setShowItems] = useState(false);

  // Notify parent when expanded to trigger fetching averages
  useEffect(() => {
    if (showItems && onExpand) {
      onExpand(true);
    }
  }, [showItems, onExpand]);
  const totalSales = locationExpense.totalSales ?? getSalesForLocation(locationExpense.location);
  const criticalItems = groupItemsByCriticalItem(locationExpense.items);
  const itemMode = itemViewMode[locationExpense.location] || 'critical';
  const expensePercent = locationExpense.expensePercentageOfSales;
  const itemCount = Math.round(locationExpense.itemCount ?? locationExpense.items.length).toLocaleString();

  return (
    <Card
      size="small"
      style={{ marginBottom: 8 }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      {/* Header with location name */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: 15, display: 'block', lineHeight: 1.3 }}>
            {locationExpense.location}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            DM: {locationExpense.dm}
          </Text>
        </div>
      </div>

      {/* Key metrics - 2x2 grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px 16px',
        padding: '12px 0',
        borderTop: '1px solid #f0f0f0',
        borderBottom: showItems ? '1px solid #f0f0f0' : 'none',
      }}>
        <div>
          <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2 }}>
            Total Sales
          </Text>
          <Text strong style={{ fontSize: 16 }}>
            {formatCurrency(totalSales)}
          </Text>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2 }}>
            Total Expense
          </Text>
          <Text strong style={{ fontSize: 16 }}>
            {formatCurrency(locationExpense.totalExpense)}
          </Text>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2 }}>
            Expense % of Sales
          </Text>
          <Text strong style={{ fontSize: 18 }}>
            {expensePercent !== undefined && expensePercent > 0
              ? `${expensePercent.toFixed(1)}%`
              : '--'}
          </Text>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2 }}>
            Item Count
          </Text>
          <Text strong style={{ fontSize: 18 }}>
            {itemCount}
          </Text>
        </div>
      </div>

      {/* Expandable items section */}
      {showItems && (
        <div style={{ paddingTop: 12 }}>
          <div style={{ marginBottom: 12 }}>
            <Radio.Group
              value={itemMode}
              onChange={(e) => setItemViewMode({ ...itemViewMode, [locationExpense.location]: e.target.value })}
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="critical">Critical Items</Radio.Button>
              <Radio.Button value="all">All Items</Radio.Button>
            </Radio.Group>
          </div>
          <div>
            {itemMode === 'all' ? (
              locationExpense.items.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: idx < locationExpense.items.length - 1 ? '1px solid #f5f5f5' : 'none',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 13, display: 'block' }}>{item.itemName}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Qty: {Math.round(item.billedQuantity).toLocaleString()}
                    </Text>
                  </div>
                  <Text
                    strong
                    style={{
                      fontSize: 14,
                      color: item.itemExpenseLevel === 'HIGH' ? '#ff4d4f' : undefined,
                      marginLeft: 8,
                    }}
                  >
                    {formatCurrency(item.totalAmount)}
                  </Text>
                </div>
              ))
            ) : (
              criticalItems.map((criticalItem, idx) => {
                // Calculate averages for this critical item (weekly view only)
                let avg4WkQty: number | null = null;
                let avg8WkQty: number | null = null;

                if (viewMode === 'weekly') {
                  const matchingItems = locationExpense.items.filter(item => {
                    const criticalItemName = CRITICAL_ITEMS_MAPPING[item.itemName] || 'Other';
                    return criticalItemName === criticalItem.criticalItemName;
                  });

                  // Calculate 4-week average
                  if (item4WeekAverages) {
                    let total4WkQty = 0;
                    let found4Wk = false;
                    matchingItems.forEach(item => {
                      const avg = item4WeekAverages[item.itemName];
                      if (avg) {
                        total4WkQty += avg.averageQuantity;
                        found4Wk = true;
                      }
                    });
                    if (found4Wk) avg4WkQty = total4WkQty;
                  }

                  // Calculate 8-week average
                  if (item8WeekAverages) {
                    let total8WkQty = 0;
                    let found8Wk = false;
                    matchingItems.forEach(item => {
                      const avg = item8WeekAverages[item.itemName];
                      if (avg) {
                        total8WkQty += avg.averageQuantity;
                        found8Wk = true;
                      }
                    });
                    if (found8Wk) avg8WkQty = total8WkQty;
                  }
                }

                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: idx < criticalItems.length - 1 ? '1px solid #f5f5f5' : 'none',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: 13, display: 'block' }}>{criticalItem.criticalItemName}</Text>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Qty: {Math.round(criticalItem.totalQuantity).toLocaleString()}
                        </Text>
                        {viewMode === 'weekly' && (
                          <>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {loadingAverages ? (
                                <Skeleton.Input active size="small" style={{ width: 50, height: 14 }} />
                              ) : avg4WkQty !== null ? (
                                `4-Wk: ${Math.round(avg4WkQty).toLocaleString()}`
                              ) : (
                                '4-Wk: --'
                              )}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {loadingAverages ? (
                                <Skeleton.Input active size="small" style={{ width: 50, height: 14 }} />
                              ) : avg8WkQty !== null ? (
                                `8-Wk: ${Math.round(avg8WkQty).toLocaleString()}`
                              ) : (
                                '8-Wk: --'
                              )}
                            </Text>
                          </>
                        )}
                      </div>
                    </div>
                    <Text strong style={{ fontSize: 14, marginLeft: 8 }}>
                      {formatCurrency(criticalItem.totalAmount)}
                    </Text>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Show more/less toggle */}
      <div
        onClick={() => setShowItems(!showItems)}
        style={{
          textAlign: 'center',
          paddingTop: 8,
          cursor: 'pointer',
          color: '#1890ff',
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        {showItems ? (
          <>Less <UpOutlined style={{ fontSize: 10 }} /></>
        ) : (
          <>View Items <DownOutlined style={{ fontSize: 10 }} /></>
        )}
      </div>
    </Card>
  );
};

